import { useState } from 'react';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';
import type { GrammarTopic } from './grammarTypes';

interface AiTutorBoxProps {
  topic: GrammarTopic;
}

function simpleMarkdown(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--theme-text,#ffffff);font-weight:700">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em style="color:#60a5fa">$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(255,255,255,0.1);padding:1px 5px;border-radius:4px;font-size:12px;color:#38bdf8">$1</code>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

export function AiTutorBox({ topic }: AiTutorBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);

  const quickQuestions = [
    '💡 В чём главная суть и логика носителя?',
    '⚔️ В чём главное отличие от похожих правил?',
    '⚠️ 3 самые частые ошибки русскоязычных',
    '⏳ Какие ключевые слова-маркеры времени?',
  ];

  const handleAsk = async (queryText: string) => {
    const q = queryText.trim();
    if (!q || loading) return;

    setLoading(true);
    setAnswer(null);

    try {
      const res = await api.post<{ answer: string }>('/grammar/ask-tutor', {
        topic_id: topic.id,
        question: q,
      });
      setAnswer(res.answer);
      setQuestion('');
    } catch (err) {
      captureError(err, { context: 'AiTutorBox.ask', topic_id: topic.id });
      setAnswer('Не удалось получить ответ репетитора. Попробуйте чуть позже.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: 16,
        background: 'var(--theme-bg-card)',
        border: '1px solid rgba(99, 102, 241, 0.35)',
        borderRadius: 'var(--theme-border-radius, 14px)',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
      }}
    >
      {/* Header Button */}
      <button
        onClick={() => setIsOpen(v => !v)}
        style={{
          width: '100%',
          padding: '13px 16px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.04) 100%)',
          border: 'none',
          color: 'var(--theme-text)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>💡</span>
          <span style={{ color: '#818cf8', fontWeight: 700 }}>Спросить AI-репетитора по этой теме</span>
        </span>
        <span
          style={{
            color: 'var(--theme-text-muted)',
            fontSize: 16,
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'none',
          }}
        >
          ▾
        </span>
      </button>

      {isOpen && (
        <div style={{ padding: '16px', borderTop: '1px solid rgba(99, 102, 241, 0.2)' }}>
          <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 10 }}>
            Быстрые вопросы репетитору:
          </div>

          {/* Quick chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {quickQuestions.map((q, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleAsk(q)}
                disabled={loading}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: 16,
                  padding: '6px 11px',
                  color: 'var(--theme-text)',
                  fontSize: 12,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Custom Question input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAsk(question);
            }}
            style={{ display: 'flex', gap: 8, marginBottom: answer ? 14 : 0 }}
          >
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Спроси своими словами (например: почему здесь was, а не were?)..."
              disabled={loading}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'var(--theme-bg-elevated)',
                border: '1px solid var(--theme-border)',
                borderRadius: 10,
                color: 'var(--theme-text)',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={loading || !question.trim()}
              style={{
                padding: '10px 16px',
                background: 'var(--theme-primary, #6366f1)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                cursor: loading || !question.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !question.trim() ? 0.5 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? 'Думает...' : 'Спросить'}
            </button>
          </form>

          {/* Answer bubble */}
          {loading && (
            <div
              style={{
                marginTop: 12,
                padding: '12px 14px',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: 12,
                border: '1px solid rgba(99, 102, 241, 0.25)',
                fontSize: 13,
                color: 'var(--theme-text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span>🤖</span>
              <span>Репетитор формулирует понятное объяснение...</span>
            </div>
          )}

          {answer && !loading && (
            <div
              style={{
                marginTop: 12,
                padding: '14px 16px',
                background: 'rgba(15, 23, 42, 0.65)',
                border: '1px solid rgba(99, 102, 241, 0.35)',
                borderRadius: 12,
                fontSize: 13,
                lineHeight: 1.5,
                color: 'var(--theme-text-secondary)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: '#818cf8', fontWeight: 700, fontSize: 12 }}>
                <span>🤖</span>
                <span>ОТВЕТ AI-РЕПЕТИТОРА:</span>
              </div>
              <div dangerouslySetInnerHTML={{ __html: simpleMarkdown(answer) }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
